/**
 * WebFavoritesPageService - Web Platform Implementation
 *
 * Web-specific implementation of IFavoritesPageService
 * Persists favorites to Supabase for cross-device sync
 */

import type {
  IFavoritesPageService,
  FavoriteItem,
  FavoriteOperationResult
} from '../../contracts'
import type { MenuItem } from '@restaurant/core'
import { getSupabaseClient, filterHiddenFromFavorites } from '@restaurant/core'
import type { RawUserFavoriteRow } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'


// Database row type for user_favorites table
interface UserFavoriteRow {
  id: string
  user_id: string
  menu_item_id: string
  restaurant_store_id: string
  product_name: string
  product_image_url: string | null
  selected_options: Record<string, unknown>[]
  selected_price_variant: string
  created_at: string
  updated_at: string
  menuitems?: {
    menu_item_id: string
    name: string
    description: string | null
    price: number
    image_url: string | null
    category_id: string | null
    price_variants: Record<string, unknown>
    display_order: number
    is_hidden: boolean
  }
}

/**
 * Web implementation of favorites page service
 * Uses Supabase for favorites persistence
 */
export class WebFavoritesPageService implements IFavoritesPageService {
  /**
   * Get user's favorite items with complete menu item data
   */
  async getFavoriteItems(userId: string, restaurantStoreId: string): Promise<FavoriteItem[]> {
    const logger = safeGetLogger()
    logger?.debug('getFavoriteItems called', { domain: 'favorites', userId, restaurantStoreId })

    try {
      const supabase = getSupabaseClient()

      const { data, error } = await supabase
        .from('user_favorites')
        .select(`
          *,
          menuitems (*)
        `)
        .eq('user_id', userId)
        .eq('restaurant_store_id', restaurantStoreId)
        .order('created_at', { ascending: false })

      const logger2 = safeGetLogger()
      logger2?.debug('Favorites query result', { domain: 'favorites', hasData: !!data, hasError: !!error })

      if (error) {
        const logger3 = safeGetLogger()
        logger3?.error('Failed to fetch favorites', { domain: 'favorites', error: String(error) })
        throw new Error('Failed to load favorite items')
      }

      const rows = ((data as UserFavoriteRow[] | null) || [])
      const visible = filterHiddenFromFavorites(rows as unknown as RawUserFavoriteRow[])
      const favoriteItems: FavoriteItem[] = (visible as unknown as UserFavoriteRow[]).map(row => this.mapRowToFavoriteItem(row))

      const logger4 = safeGetLogger()
      logger4?.favorites('loaded', { count: favoriteItems.length })
      return favoriteItems
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get favorite items', { domain: 'favorites', error: String(error) })
      throw new Error('Failed to load favorite items')
    }
  }

  /**
   * Remove item from favorites
   */
  async removeFavorite(itemId: string, userId: string): Promise<FavoriteOperationResult> {
    try {
      const supabase = getSupabaseClient()

      const { error } = await supabase
        .from('user_favorites')
        .delete()
        .eq('id', itemId)
        .eq('user_id', userId)

      if (error) {
        const logger = safeGetLogger()
        logger?.error('Failed to remove favorite', { domain: 'favorites', error: String(error) })
        return {
          success: false,
          message: 'Failed to remove item from favorites',
        }
      }

      return {
        success: true,
        message: 'Item removed from favorites',
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to remove favorite', { domain: 'favorites', error: String(error) })
      return {
        success: false,
        message: 'Failed to remove item from favorites',
      }
    }
  }

  /**
   * Search favorite items by query
   */
  async searchFavorites(favorites: FavoriteItem[], query: string): Promise<FavoriteItem[]> {
    const searchTerm = query.toLowerCase().trim()

    if (!searchTerm) {
      return []
    }

    return favorites.filter(item =>
      item.productName.toLowerCase().includes(searchTerm) ||
      (item.productOptions && item.productOptions.toLowerCase().includes(searchTerm)) ||
      (item.menuItem.description && item.menuItem.description.toLowerCase().includes(searchTerm))
    )
  }

  /**
   * Filter favorites by category or type
   */
  async filterFavorites(favorites: FavoriteItem[], category?: string): Promise<FavoriteItem[]> {
    if (!category) {
      return favorites
    }

    return favorites.filter(item =>
      item.menuItem.categoryId === category
    )
  }

  /**
   * Navigate to product details - implementation depends on platform router
   */
  navigateToProduct(itemId: string, _restaurantId: string): void {
    // For web implementation, use browser navigation
    const productUrl = `/menu?item=${itemId}`
    window.location.href = productUrl
  }

  /**
   * Add favorite item to cart
   */
  async addToCart(itemId: string, userId: string): Promise<FavoriteOperationResult> {
    try {
      const supabase = getSupabaseClient()

      // Get the favorite item details
      const { data: favoriteItem, error } = await supabase
        .from('user_favorites')
        .select(`
          *,
          menuitems (*)
        `)
        .eq('id', itemId)
        .eq('user_id', userId)
        .maybeSingle()

      if (error || !favoriteItem) {
        return {
          success: false,
          message: 'Favorite item not found',
        }
      }

      const menuItemRow = favoriteItem.menuitems as { is_hidden?: boolean } | undefined
      if (!menuItemRow || menuItemRow.is_hidden === true) {
        return {
          success: false,
          message: 'Favorite item not found',
        }
      }

      const logger = safeGetLogger()
      logger?.favorites('add_to_cart', {
        menuItem: favoriteItem.menuitems,
        options: favoriteItem.selected_options,
        priceVariant: favoriteItem.selected_price_variant
      })

      return {
        success: true,
        message: 'Item added to cart',
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to add favorite to cart', { domain: 'favorites', error: String(error) })
      return {
        success: false,
        message: 'Failed to add item to cart',
      }
    }
  }

  /**
   * Get categories available in favorites for filtering
   */
  getAvailableCategories(favorites: FavoriteItem[]): string[] {
    const categories = new Set<string>()

    favorites.forEach(item => {
      if (item.menuItem.categoryId) {
        categories.add(item.menuItem.categoryId)
      }
    })

    return Array.from(categories).sort()
  }

  /**
   * Refresh favorites data from server
   */
  async refreshFavorites(userId: string, restaurantStoreId: string): Promise<FavoriteItem[]> {
    return this.getFavoriteItems(userId, restaurantStoreId)
  }

  /**
   * Add a new favorite item
   */
  async addFavorite(
    menuItem: MenuItem,
    userId: string,
    restaurantStoreId: string,
    selectedOptions?: Record<string, unknown>[],
    selectedPriceVariant?: string
  ): Promise<FavoriteOperationResult> {
    try {
      const supabase = getSupabaseClient()

      // Check if already favorited
      const { data: existing } = await supabase
        .from('user_favorites')
        .select('id')
        .eq('user_id', userId)
        .eq('menu_item_id', menuItem.id)
        .eq('restaurant_store_id', restaurantStoreId)
        .maybeSingle()

      if (existing) {
        return {
          success: false,
          message: 'Item is already in favorites',
        }
      }

      const { error } = await supabase
        .from('user_favorites')
        .insert({
          user_id: userId,
          menu_item_id: menuItem.id,
          restaurant_store_id: restaurantStoreId,
          product_name: menuItem.name,
          product_image_url: menuItem.imageUrl || null,
          selected_options: selectedOptions || [],
          selected_price_variant: selectedPriceVariant || 'base',
        })

      if (error) {
        const logger = safeGetLogger()
        logger?.error('Failed to add favorite', { domain: 'favorites', error: String(error) })
        return {
          success: false,
          message: 'Failed to add item to favorites',
        }
      }

      return {
        success: true,
        message: 'Item added to favorites',
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to add favorite', { domain: 'favorites', error: String(error) })
      return {
        success: false,
        message: 'Failed to add item to favorites',
      }
    }
  }

  /**
   * Check if an item is favorited
   */
  async isFavorited(menuItemId: string, userId: string, restaurantStoreId: string): Promise<boolean> {
    try {
      const supabase = getSupabaseClient()

      const { data, error } = await supabase
        .from('user_favorites')
        .select('id')
        .eq('user_id', userId)
        .eq('menu_item_id', menuItemId)
        .eq('restaurant_store_id', restaurantStoreId)
        .maybeSingle()

      if (error) {
        const logger = safeGetLogger()
        logger?.error('Failed to check favorite status', { domain: 'favorites', error: String(error) })
        return false
      }

      return !!data
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to check favorite status', { domain: 'favorites', error: String(error) })
      return false
    }
  }

  /**
   * Toggle favorite status for an item
   */
  async toggleFavorite(
    menuItem: MenuItem,
    userId: string,
    restaurantStoreId: string,
    selectedOptions?: Record<string, unknown>[],
    selectedPriceVariant?: string
  ): Promise<FavoriteOperationResult> {
    const isFav = await this.isFavorited(menuItem.id, userId, restaurantStoreId)

    if (isFav) {
      // Need to get the favorite id first to remove it
      const supabase = getSupabaseClient()
      const { data } = await supabase
        .from('user_favorites')
        .select('id')
        .eq('user_id', userId)
        .eq('menu_item_id', menuItem.id)
        .eq('restaurant_store_id', restaurantStoreId)
        .maybeSingle()

      if (data) {
        return this.removeFavorite(data.id, userId)
      }
      return { success: false, message: 'Favorite not found' }
    } else {
      return this.addFavorite(menuItem, userId, restaurantStoreId, selectedOptions, selectedPriceVariant)
    }
  }

  /**
   * Map database row to FavoriteItem interface
   */
  private mapRowToFavoriteItem(row: UserFavoriteRow): FavoriteItem {
    const menuItemData = row.menuitems!

    // Build MenuItem from database data
    const menuItem: MenuItem = {
      id: menuItemData.menu_item_id,
      cloverId: '',
      priceVariants: {},
      name: menuItemData.name,
      description: menuItemData.description || undefined,
      price: Number(menuItemData.price),
      imageUrl: menuItemData.image_url || undefined,
      categoryId: menuItemData.category_id || '',
      customizations: [],
      isPopular: false,
      isFeatured: false,
      isVegetarian: false,
      isVegan: false,
      isGlutenFree: false,
      allergens: [],
      tags: [],
      displayOrder: menuItemData.display_order,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    // Format selected options as a string for display
    const optionsString = row.selected_options?.length > 0
      ? this.formatOptionsForDisplay(row.selected_options)
      : undefined

    return {
      id: row.id,
      menuItem,
      productName: row.product_name,
      productOptions: optionsString,
      productImageUrl: row.product_image_url || menuItem.imageUrl,
      isFavorited: true,
      dateAdded: new Date(row.created_at),
      rawSelectedOptions: row.selected_options || [],
      selectedPriceVariant: row.selected_price_variant || 'base',
    }
  }

  /**
   * Format selected options array to display string
   */
  private formatOptionsForDisplay(options: Record<string, unknown>[]): string {
    // This should match the format used by the cart/order system
    // For now, provide a basic string representation
    if (!options || options.length === 0) return ''

    return options
      .map(opt => {
        if (typeof opt === 'object' && opt !== null) {
          return (opt as { name?: string }).name || JSON.stringify(opt)
        }
        return String(opt)
      })
      .filter(Boolean)
      .join(', ')
  }
}
